/**
 * Recording-session API calls (per-take QA upload, verbatim, session config).
 * Kept out of AppContext: these are session-scoped, not app-global state.
 */

import { TakeStyle } from './prompts';
import { TakeStats } from './recorder';
import { authHeaders, requestJson } from '../api/http';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export interface QualityReport {
  duration_seconds: number;
  loudness_db: number;
  signal_quality?: string;
  clipping?: string;
  clipping_percent?: number;
  silence_percent?: number;
  snr_db?: number;
  sample_rate?: number;
}

export interface SampleUploadResult {
  accepted: boolean;
  sample_index: number;
  filename: string;
  voice_id: string;
  quality_report: QualityReport;
  rejection_reasons?: string[] | null;
  suggestions?: string[] | null;
  message: string;
  s3_key?: string | null;
  storage_confirmed: boolean;
}

function validateUploadResponse(
  result: SampleUploadResult,
  storageRequired: boolean
): SampleUploadResult {
  if ((result.accepted || storageRequired) && (!result.storage_confirmed || !result.s3_key)) {
    throw new Error('The server could not confirm that recording was saved. Please retry.');
  }
  return result;
}

export async function uploadTake(params: {
  voiceId: string;
  sampleIndex: number;
  style: TakeStyle;
  wavBlob: Blob;
  stats: TakeStats;
  voiceName?: string;
  forced?: boolean;
}): Promise<SampleUploadResult> {
  const form = new FormData();
  form.append('voice_id', params.voiceId);
  form.append('sample_index', String(params.sampleIndex));
  form.append('style', params.style);
  form.append('forced', String(params.forced ?? false));
  form.append('dsp_suppressed', String(params.stats.dspSuppressed));
  if (params.voiceName) form.append('voice_name', params.voiceName);
  form.append(
    'file',
    new File([params.wavBlob], `take_${params.style}_${params.sampleIndex}.wav`, {
      type: 'audio/wav',
    })
  );

  const result = await requestJson<SampleUploadResult>(`${API_BASE_URL}/voice/upload-single-sample`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  return validateUploadResponse(result, params.forced === true);
}

export async function uploadVerbatim(params: {
  voiceId: string;
  listenerId?: string;
  childName: string;
  clipKey: 'intro' | 'goodnight' | 'name';
  wavBlob: Blob;
  forced?: boolean;
}): Promise<SampleUploadResult> {
  const form = new FormData();
  form.append('voice_id', params.voiceId);
  if (params.listenerId) form.append('listener_id', params.listenerId);
  form.append('child_name', params.childName);
  form.append('clip_key', params.clipKey);
  form.append('forced', String(params.forced ?? false));
  form.append(
    'file',
    new File([params.wavBlob], `${params.clipKey}.wav`, { type: 'audio/wav' })
  );

  const result = await requestJson<SampleUploadResult>(`${API_BASE_URL}/voice/upload-verbatim`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  return validateUploadResponse(result, params.forced === true);
}

export interface CaptureSessionStatus {
  voice_id: string;
  listener_id: string | null;
  complete: boolean;
  expected_sample_count: number;
  persisted_sample_indexes: number[];
  expected_verbatim_keys: Array<'intro' | 'goodnight' | 'name'>;
  persisted_verbatim_keys: Array<'intro' | 'goodnight' | 'name'>;
  missing: string[];
}

export async function fetchCaptureStatus(
  voiceId: string,
  childName?: string,
  listenerId?: string
): Promise<CaptureSessionStatus> {
  const params = new URLSearchParams();
  if (childName?.trim()) params.set('child_name', childName.trim());
  if (listenerId) params.set('listener_id', listenerId);
  const query = params.toString();
  return requestJson<CaptureSessionStatus>(
    `${API_BASE_URL}/voice/capture-status/${encodeURIComponent(voiceId)}${query ? `?${query}` : ''}`,
    { headers: authHeaders() }
  );
}

export interface SessionConfig {
  capture_version: number;
  qa_version: string;
  mic_check: {
    room_tone_s: number;
    speech_s: number;
    sentence: string;
    snr_pass_db: number;
    snr_warn_db: number;
    peak_max_db: number;
  };
  take_prompts: Array<{
    style: TakeStyle;
    title: string;
    coaching: string;
    text: string;
    min_s: number;
    max_s: number;
  }>;
  verbatim_prompts: Array<{
    key: 'intro' | 'goodnight' | 'name';
    title: string;
    template: string;
    coaching: string;
  }>;
}

export async function fetchSessionConfig(): Promise<SessionConfig | null> {
  try {
    return await requestJson<SessionConfig>(`${API_BASE_URL}/voice/session-config`);
  } catch {
    return null; // fall back to the local prompts.ts constants
  }
}
